import React from 'react';
import { Box, ButtonBase, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import type { TableCellProps } from '@mui/material/TableCell';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import { Tooltip } from 'framework/ui/mui-builder';

export type AnalyticalTableColumn<ColumnId extends string = string> = {
  id: ColumnId;
  label: React.ReactNode;
  align?: TableCellProps['align'];
};

export type AnalyticalTableRow<ColumnId extends string, CellData> = {
  id: string;
  label: React.ReactNode;
  isSummary?: boolean;
  cells: Record<ColumnId, CellData>;
};

type AnalyticalTableCellParams<ColumnId extends string, CellData> = {
  row: AnalyticalTableRow<ColumnId, CellData>;
  column: AnalyticalTableColumn<ColumnId>;
  cell: CellData;
};

type AnalyticalTableLayoutProps<ColumnId extends string, CellData> = {
  columns: AnalyticalTableColumn<ColumnId>[];
  rows: AnalyticalTableRow<ColumnId, CellData>[];
  renderCell: (params: AnalyticalTableCellParams<ColumnId, CellData>) => React.ReactNode;
  renderCellTooltip?: (params: AnalyticalTableCellParams<ColumnId, CellData>) => React.ReactNode;
  getCellBackgroundColor?: (params: AnalyticalTableCellParams<ColumnId, CellData>) => string;
  isCellInteractive?: (params: AnalyticalTableCellParams<ColumnId, CellData>) => boolean;
  onCellClick?: (params: AnalyticalTableCellParams<ColumnId, CellData>) => void;
  tableSx?: SystemStyleObject<Theme>;
  labelCellSx?: SystemStyleObject<Theme>;
  cellSx?: SystemStyleObject<Theme>;
};

const BASE_CELL_SX: SystemStyleObject<Theme> = {
  height: '50px',
  border: '1px solid',
  borderColor: 'border.default',
  color: 'text.primary',
  fontSize: '14px',
};

const HEADER_CELL_SX: SystemStyleObject<Theme> = {
  ...BASE_CELL_SX,
  border: 0,
  color: 'primary.main',
};

const LABEL_CELL_SX: SystemStyleObject<Theme> = {
  ...BASE_CELL_SX,
  width: '210px',
};

const SUMMARY_CELL_SX: SystemStyleObject<Theme> = {
  fontWeight: 600,
};

const CELL_CONTENT_SX: SystemStyleObject<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const INTERACTIVE_CELL_CONTENT_SX: SystemStyleObject<Theme> = {
  width: '100%',
  minHeight: '50px',
  fontWeight: 'inherit',
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: '-2px',
  },
};

const AnalyticalTableLayout = <ColumnId extends string, CellData>({
  columns,
  rows,
  renderCell,
  renderCellTooltip,
  getCellBackgroundColor,
  isCellInteractive,
  onCellClick,
  tableSx,
  labelCellSx,
  cellSx,
}: AnalyticalTableLayoutProps<ColumnId, CellData>) => {
  const renderValueCell = (row: AnalyticalTableRow<ColumnId, CellData>, column: AnalyticalTableColumn<ColumnId>) => {
    const cell = row.cells[column.id];
    const params = { row, column, cell };
    const isInteractive = Boolean(onCellClick && isCellInteractive?.(params));
    const content = (
      <Box component="span" sx={CELL_CONTENT_SX}>
        {renderCell(params)}
      </Box>
    );
    const wrappedContent = isInteractive ? (
      <ButtonBase type="button" sx={INTERACTIVE_CELL_CONTENT_SX} onClick={() => onCellClick?.(params)}>
        {content}
      </ButtonBase>
    ) : (
      content
    );
    const tooltip = renderCellTooltip?.(params);

    return (
      <TableCell
        key={`${row.id}-${column.id}`}
        align={column.align || 'center'}
        sx={{
          ...BASE_CELL_SX,
          ...(row.isSummary ? SUMMARY_CELL_SX : {}),
          ...(isInteractive ? { padding: 0 } : {}),
          ...(getCellBackgroundColor ? { background: getCellBackgroundColor(params) } : {}),
          ...cellSx,
        }}
      >
        {tooltip ? (
          <Tooltip title={tooltip} placement="top">
            {wrappedContent}
          </Tooltip>
        ) : (
          wrappedContent
        )}
      </TableCell>
    );
  };

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table
        size="small"
        sx={{
          borderCollapse: 'collapse',
          ...tableSx,
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={HEADER_CELL_SX} />
            {columns.map(column => (
              <TableCell key={column.id} align={column.align || 'center'} sx={HEADER_CELL_SX}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.id}>
              <TableCell
                component="th"
                scope="row"
                sx={{
                  ...LABEL_CELL_SX,
                  ...(row.isSummary ? SUMMARY_CELL_SX : {}),
                  ...labelCellSx,
                }}
              >
                {row.label}
              </TableCell>
              {columns.map(column => renderValueCell(row, column))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AnalyticalTableLayout;
